import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faChevronDown, faChevronRight, faPlus, faXmark} from '@fortawesome/free-solid-svg-icons';
import {BindingCandidate, JsonSchema, WidgetName} from './schema-form.types';
import {enumLabel, formatCardTitle, matchesVisibleWhen, resolveWidget} from './widget-registry';
import {SfFieldWrapperComponent} from './field-wrapper.component';
import {SfToggleComponent} from './widgets/sf-toggle.component';
import {SfPromptOptimizeComponent} from './widgets/sf-prompt-optimize.component';
import {SfLeafComponent} from './widgets/sf-leaf.component';
import {SfValueBindingComponent} from './widgets/sf-value-binding.component';

/**
 * Recursive dispatcher: renders ONE schema property.
 *
 * Leaf types delegate to `sf-leaf` inside a label/helper wrapper.
 * `object` and `object-array` recurse into this same component (self-import), so
 * nested schemas and object-array repeaters are handled without any cross-file
 * component cycle.
 */
@Component({
  selector: 'app-schema-field',
  standalone: true,
  host: {'class': 'block w-full'},
  imports: [
    FaIconComponent,
    SfFieldWrapperComponent,
    SfToggleComponent,
    SfPromptOptimizeComponent,
    SfLeafComponent,
    SfValueBindingComponent,
    SchemaFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @switch (widget) {
      @case ('object') {
        <div class="flex flex-col gap-4 w-full">
          @if (showLabel && label) {
            <label class="text-sm font-medium text-gray-900">{{ label }}</label>
          }
          @for (k of objectKeys; track k) {
            <app-schema-field
              [schema]="propAt(k)"
              [value]="objValue[k]"
              [resetKey]="resetKey"
              (valueChange)="onObjectField(k, $event)">
            </app-schema-field>
          }
        </div>
      }
      @case ('toggle') {
        <!-- Boolean: Label links, Switch rechts in einer Zeile -->
        <div class="flex items-center justify-between gap-4 w-full">
          <div class="flex flex-col gap-0.5 min-w-0">
            @if (showLabel && label) {
              <label class="text-sm font-medium text-gray-900">{{ label }}</label>
            }
            @if (description) {
              <p class="text-xs text-gray-500 leading-tight">{{ description }}</p>
            }
          </div>
          <sf-toggle [schema]="schema" [value]="value" (valueChange)="valueChange.emit($event)" class="shrink-0"></sf-toggle>
        </div>
      }
      @case ('value-binding') {
        <!-- Bindbarer Wert: Festwert ODER Ausgabe eines vorherigen Schritts. Die Kandidaten
             injiziert der Aufrufer als x-binding-candidates (siehe schema-form.types). -->
        <sf-field-wrapper [label]="showLabel ? label : ''" [description]="description">
          <sf-value-binding
            [schema]="schema"
            [value]="value"
            [candidates]="bindingCandidates"
            [readOnly]="disabled"
            (literalChange)="valueChange.emit($event)"
            (referenceChange)="valueChange.emit($event)"
            (cleared)="valueChange.emit(null)">
          </sf-value-binding>
        </sf-field-wrapper>
      }
      @case ('object-array') {
        <div class="flex flex-col gap-2 w-full">
          @if (showLabel && label) {
            <label class="text-sm font-medium text-gray-900">{{ label }}</label>
          }
          @if (description) {
            <p class="text-xs text-gray-500 leading-tight">{{ description }}</p>
          }
          @for (row of arrayValue; track rowIndex; let rowIndex = $index) {
            @if (isCardLayout) {
              <!-- Karte je Eintrag: zugeklappt nur die Kopfzeile, aufgeklappt die Felder (cardLines).
                   Bei einem Knoten mit mehreren Ausgabeparametern oder Fällen ist die Liste sonst
                   länger als das Panel und die einzelne Karte nicht mehr als Einheit erkennbar. -->
              <div class="flex flex-col w-full border border-gray-300 rounded-lg bg-white">
                <div (click)="toggleCard(rowIndex)" (keydown.enter)="toggleCard(rowIndex)"
                     [attr.aria-expanded]="isCardOpen(rowIndex)" role="button" tabindex="0"
                     class="group flex items-center gap-2 px-3 py-2 cursor-pointer select-none">
                  <fa-icon [icon]="isCardOpen(rowIndex) ? faChevronDown : faChevronRight"
                           class="text-xs text-gray-400 shrink-0"></fa-icon>
                  <span class="text-sm font-medium text-gray-900 truncate flex-1 min-w-0">
                    {{ cardTitle(row, rowIndex) }}
                  </span>
                  @if (!isRowLocked(row)) {
                    <button type="button" (click)="$event.stopPropagation(); removeRow(rowIndex)"
                            [attr.aria-label]="'Eintrag entfernen'"
                            class="text-gray-400 hover:text-red transition-colors shrink-0 opacity-0 group-hover:opacity-100">
                      <fa-icon [icon]="faXmark"></fa-icon>
                    </button>
                  }
                </div>
                @if (isCardOpen(rowIndex)) {
                  <div class="flex flex-col gap-4 px-3 py-3 border-t border-gray-200">
                    @for (line of cardLines(row); track lineIndex; let lineIndex = $index) {
                      <div class="flex items-start gap-4 w-full">
                        @for (k of line; track k) {
                          <div class="flex-1 min-w-0">
                            <app-schema-field
                              [schema]="itemPropFor(row, k)"
                              [value]="row[k]"
                              [disabled]="isRowLocked(row)"
                              [resetKey]="resetKey"
                              (valueChange)="onRowField(rowIndex, k, $event)">
                            </app-schema-field>
                          </div>
                        }
                      </div>
                    }
                    @if (isRowLocked(row)) {
                      <p class="text-xs text-gray-500 leading-tight">
                        Wird automatisch verwaltet und steuert die Ausgänge des Knotens.
                      </p>
                    }
                  </div>
                }
              </div>
            } @else {
              <div class="group flex items-center gap-2">
                <div class="flex-1 flex items-center gap-2">
                  @for (k of itemKeysFor(row); track k) {
                    <div [class]="itemFieldClass(row, k)">
                      <app-schema-field
                        [schema]="itemPropFor(row, k)"
                        [value]="row[k]"
                        [showLabel]="false"
                        [resetKey]="resetKey"
                        (valueChange)="onRowField(rowIndex, k, $event)">
                      </app-schema-field>
                    </div>
                  }
                </div>
                <button type="button" (click)="removeRow(rowIndex)"
                        class="text-gray-400 hover:text-red transition-colors shrink-0 opacity-0 group-hover:opacity-100">
                  <fa-icon [icon]="faXmark"></fa-icon>
                </button>
              </div>
            }
          }
          <button type="button" (click)="addRow()"
                  class="inline-flex items-center justify-center gap-2 self-center h-[38px] px-3 py-2 rounded-lg
                         text-sm font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <fa-icon [icon]="faPlus"></fa-icon>{{ addLabel }}
          </button>
        </div>
      }
      @default {
        <sf-field-wrapper [label]="showLabel ? label : ''" [description]="description"
                          [hasHeaderAction]="isOptimizable">
          @if (isOptimizable) {
            <sf-prompt-optimize headerAction [value]="value"
                                (valueChange)="valueChange.emit($event)"></sf-prompt-optimize>
          }
          <sf-leaf [schema]="schema" [value]="value" [disabled]="disabled"
                   (valueChange)="valueChange.emit($event)"></sf-leaf>
        </sf-field-wrapper>
      }
    }
  `,
})
export class SchemaFieldComponent implements OnChanges {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  @Input() showLabel = true;
  /** Aus, wenn der Aufrufer die Beschreibung selbst rendert (z.B. gemeinsam mit dem Port-Status). */
  @Input() showDescription = true;
  /** Read-only rendern — gesetzt für Zeilen, die das Modul selbst verwaltet (`x-managed-items`). */
  @Input() disabled = false;
  /**
   * Kennung des Kontexts, zu dem die gerenderten Werte gehören (im Editor: die Knoten-ID). Ändert
   * sie sich, verwirft die Komponente ihren Anzeigezustand — siehe {@link ngOnChanges}.
   */
  @Input() resetKey: string | null = null;
  @Output() valueChange = new EventEmitter<any>();

  protected readonly faPlus = faPlus;
  protected readonly faXmark = faXmark;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faChevronRight = faChevronRight;

  /**
   * Aufgeklappte Karten eines `object-array` (Index im Array).
   *
   * Zugeklappt ist der Ausgangszustand: die Karte zeigt dann nur ihren Titel, und ein Knoten mit
   * mehreren Ausgabeparametern oder Fällen bleibt auf einen Blick lesbar. Aufgeklappt wird per
   * Klick — und automatisch für einen neu hinzugefügten Eintrag, der sonst leer und unsichtbar
   * unten anhinge.
   */
  private openCards = new Set<number>();

  /**
   * Knotenwechsel heißt andere Einträge — der Aufklappzustand gilt dann nicht mehr.
   *
   * Bewusst an einem vom Aufrufer gesetzten Schlüssel (dem Knoten) und NICHT an der Identität von
   * `schema`: das aufgelöste Schema wird in `workflow-node-config.resolveProp` für jedes bindbare
   * Feld neu gebaut und ist damit in jedem Change-Detection-Lauf ein anderes Objekt. Daran
   * gehängt fiel jede Karte sofort wieder zu — sichtbar genau bei Bedingung und Variable, deren
   * Einträge ein `value-binding`-Feld enthalten, während die KI-Anweisung ohne ein solches Feld
   * funktionierte.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetKey']) this.openCards.clear();
  }

  get widget(): WidgetName {
    return resolveWidget(this.schema);
  }

  get label(): string {
    return this.schema?.title ?? '';
  }

  get description(): string {
    return this.showDescription ? (this.schema?.description ?? '') : '';
  }

  // --- object ---------------------------------------------------------------
  get objValue(): Record<string, any> {
    return this.value && typeof this.value === 'object' && !Array.isArray(this.value) ? this.value : {};
  }

  /**
   * Sichtbare Felder eines Objekt-Schemas (Wurzel-Ebene).
   *
   * `x-visible-when` wird hier genau wie bei einem Array-Eintrag gegen die eigenen Geschwister-
   * Properties ausgewertet (`objValue` statt `row`) — z. B. schaltet „Prüfmodus" beim
   * IFC-Prüfung-Knoten die passenden Felder frei, ohne dass sie in einem Array stecken müssen.
   */
  get objectKeys(): string[] {
    const props = this.schema?.properties ?? {};
    return Object.keys(props).filter(k => {
      const prop = (props as any)[k];
      if (prop?.['x-hidden'] === true) return false;
      return this.isVisibleIn(prop, this.objValue);
    });
  }

  // --- object-array ---------------------------------------------------------
  get itemSchema(): JsonSchema {
    return this.schema?.items ?? {};
  }

  /** Statische Feldliste (ohne Eintragsbezug) — für Aufrufer ohne Zeilenkontext. */
  get itemKeys(): string[] {
    return this.itemKeysFor(undefined);
  }

  /**
   * Sichtbare Felder eines Array-Eintrags.
   *
   * `row` ist nötig, weil ein Feld über `x-visible-when` von einer Schwester-Property im SELBEN
   * Eintrag abhängen darf (KI-Anweisung: „Optionen" und „Als Verzweigung nutzen" nur bei
   * Datentyp „Auswahl"). Ohne das Argument gilt nur der statische `x-hidden`-Filter.
   */
  itemKeysFor(row?: any): string[] {
    const props = this.itemSchema.properties ?? {};
    return Object.keys(props).filter(k => {
      const prop = (props as any)[k];
      if (prop?.['x-hidden'] === true) return false;
      return this.isVisibleIn(prop, row);
    });
  }

  /** Karten-Layout statt kompakter Zeile (z.B. KI-Anweisung: Ausgabeparameter). */
  get isCardLayout(): boolean {
    return this.schema?.['x-item-layout'] === 'card';
  }

  /**
   * Wählbare Vorgängerwerte des `value-binding`-Widgets.
   *
   * Kommen als `x-binding-candidates` zur Renderzeit ins Schema (workflow-node-config); diese
   * Schicht bleibt damit workflow-agnostisch und kennt nur „ein Wert ist bindbar".
   */
  get bindingCandidates(): BindingCandidate[] {
    const candidates = this.schema?.['x-binding-candidates'];
    return Array.isArray(candidates) ? candidates : [];
  }

  /**
   * `x-visible-when: {property, equals}` — Feld nur zeigen, wenn die genannte Schwester-Property
   * im selben Eintrag den Wert hat. Ohne Zeilenkontext (`row === undefined`) wird nicht gefiltert,
   * damit die statische Feldliste vollständig bleibt.
   */
  private isVisibleIn(prop: any, row?: any): boolean {
    if (row === undefined) return true;
    return matchesVisibleWhen(prop, row);
  }

  /**
   * Karten-Layout: die sichtbaren Felder eines Eintrags, zeilenweise gruppiert.
   *
   * **Die Schema-Reihenfolge bleibt erhalten.** Ein `x-full-width`-Feld bekommt seine eigene
   * Zeile und bricht die laufende um; alle anderen sammeln sich zu je einer Zeile. Damit ist die
   * Reihenfolge im Schema zugleich die Reihenfolge in der Karte — beim Bedingungs-Modul steht so
   * der Name (ganzzeilig) über der Vergleichszeile „Wert A · Operator · Wert B · Datentyp".
   *
   * Vorher waren es zwei feste Blöcke (erst alle geteilten Felder, dann alle ganzzeiligen), ein
   * ganzzeiliges Feld konnte also nie oben stehen.
   */
  cardLines(row?: any): string[][] {
    const lines: string[][] = [];
    let current: string[] = [];
    for (const key of this.itemKeysFor(row)) {
      if (this.itemPropAt(key)['x-full-width'] === true) {
        if (current.length) lines.push(current);
        current = [];
        lines.push([key]);
        continue;
      }
      current.push(key);
    }
    if (current.length) lines.push(current);
    return lines;
  }

  /** Textfeld mit KI-Optimierung in der Label-Zeile. */
  get isOptimizable(): boolean {
    return this.schema?.['x-optimizable'] === true;
  }

  get arrayValue(): any[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  get addLabel(): string {
    return this.schema?.['x-add-label'] ?? 'Hinzufügen';
  }

  /**
   * Zeilen, die das Modul selbst verwaltet: gesperrt und nicht löschbar.
   *
   * Die Schlüssel kommen als `x-locked-keys` aus dem Panel, das dafür `x-managed-items` gegen die
   * Live-Settings auflöst (siehe workflow-node-config.resolvedSettingsProp). Diese Komponente
   * bleibt damit generisch — sie kennt weder Modul noch Setting.
   */
  isRowLocked(row: any): boolean {
    const lockedKeys: string[] = this.schema?.['x-locked-keys'] ?? [];
    if (!lockedKeys.length) return false;
    const keyProp = this.schema?.['x-locked-key-property'] ?? 'name';
    return lockedKeys.includes(String(row?.[keyProp] ?? ''));
  }

  /**
   * Titel der Karte im zugeklappten Zustand: die per `x-card-title` benannte Eintrags-Property.
   *
   * Ist diese Property ein Enum (z. B. IFC-Selektion: `facette`), wird ihr `x-enumLabels`-Label
   * gezeigt, nicht der technische Wert - sonst stuende dort "ki_suche" statt "KI-Suche".
   * Eine `x-card-title-template` hat Vorrang („Entität: IfcWall"), solange sie etwas zu zeigen hat.
   */
  cardTitle(row: any, index: number): string {
    const templated = formatCardTitle(this.schema?.['x-card-title-template'], row, this.itemSchema);
    if (templated) return templated;
    const key = this.schema?.['x-card-title'] ?? 'name';
    const raw = row?.[key];
    const propSchema = this.itemSchema?.properties?.[key];
    const resolved = Array.isArray(propSchema?.enum) ? enumLabel(propSchema, raw) : raw;
    const text = resolved === null || resolved === undefined ? '' : String(resolved).trim();
    return text || `Eintrag ${index + 1}`;
  }

  isCardOpen(index: number): boolean {
    return this.openCards.has(index);
  }

  toggleCard(index: number): void {
    if (this.openCards.has(index)) this.openCards.delete(index);
    else this.openCards.add(index);
  }

  propAt(key: string): JsonSchema {
    return this.schema?.properties?.[key] ?? {};
  }

  onObjectField(key: string, value: any): void {
    this.valueChange.emit({...this.objValue, [key]: value});
  }

  itemPropAt(key: string): JsonSchema {
    return this.itemSchema.properties?.[key] ?? {};
  }

  /**
   * Schema einer Eintrags-Property IM KONTEXT ihrer Zeile.
   *
   * `x-schema-when: {property, cases}` lässt eine Property ihre Form von einer Schwester-Property
   * im selben Eintrag beziehen — beim Variablen-Modul bestimmt der Datentyp, womit der Wert
   * erfasst wird (Zahlenfeld, Wahr/Falsch-Auswahl, Liste als Chips). Die Fallschablone wird über
   * das Basis-Schema gelegt, `x-widget: value-binding` bleibt also erhalten und nur das
   * Festwert-Feld darunter wechselt.
   */
  itemPropFor(row: any, key: string): JsonSchema {
    const base = this.itemPropAt(key);
    const when = base['x-schema-when'];
    if (!when?.property || !when.cases) return base;
    const variant = when.cases[String(row?.[when.property] ?? '')];
    return variant ? {...base, ...variant} : base;
  }

  /** Width for a repeater cell: compact controls fixed (~120px), text-like flexible. */
  itemFieldClass(row: any, key: string): string {
    const w = resolveWidget(this.itemPropFor(row, key));
    const compact = w === 'select' || w === 'status' || w === 'number' || w === 'toggle';
    return compact ? 'w-[120px] shrink-0' : 'flex-1 min-w-0';
  }

  /**
   * Eine Eintrags-Property ändern.
   *
   * `x-resets` auf der geänderten Property leert die dort genannten Schwester-Properties mit.
   * Nötig, wo eine Property die Form einer anderen bestimmt (`x-schema-when`): der alte Wert
   * passt nach dem Wechsel nicht mehr zum Feld und würde als unsichtbarer Rest weiterlaufen —
   * eine Liste, die als Text gespeichert bleibt, während das Feld leer aussieht.
   */
  onRowField(index: number, key: string, value: any): void {
    const resets: string[] = this.itemPropAt(key)['x-resets'] ?? [];
    const next = this.arrayValue.map((row, i) => {
      if (i !== index) return row;
      const updated = {...row, [key]: value};
      if (row?.[key] !== value) for (const k of resets) updated[k] = null;
      return updated;
    });
    this.valueChange.emit(next);
  }

  addRow(): void {
    this.openCards.add(this.arrayValue.length);
    this.valueChange.emit([...this.arrayValue, this.buildDefault(this.itemSchema)]);
  }

  removeRow(index: number): void {
    // Die Karten hängen am Index — nach dem Entfernen rutscht alles dahinter eine Position vor.
    const shifted = new Set<number>();
    for (const open of this.openCards) {
      if (open < index) shifted.add(open);
      else if (open > index) shifted.add(open - 1);
    }
    this.openCards = shifted;
    this.valueChange.emit(this.arrayValue.filter((_, i) => i !== index));
  }

  private buildDefault(schema: JsonSchema): Record<string, any> {
    return buildNewItem(schema);
  }
}

/**
 * Ein neuer Listeneintrag: jedes Feld mit seinem Startwert.
 *
 * `x-new-item-default` vor `default` – so kann ein Eintrag NEU anders starten, als ein gespeicherter
 * Eintrag ohne den Schlüssel gelesen wird (IFC-Prüfung: neue Karten `ids_eigenschaft`, gespeicherte
 * Altkarten ohne `facette` bleiben „eigenschaft"). Ohne Startwert: Listen leer, Objekte `null`
 * (kein leerer Text an einem Objektfeld wie dem IDS-Wertfeld), alles andere `''` wie bisher.
 */
export function buildNewItem(schema: JsonSchema): Record<string, any> {
  const obj: Record<string, any> = {};
  for (const key of Object.keys(schema.properties ?? {})) {
    const prop = schema.properties![key];
    const start = 'x-new-item-default' in prop ? prop['x-new-item-default'] : prop.default;
    obj[key] = start ?? (prop.type === 'array' ? [] : prop.type === 'object' ? null : '');
  }
  return obj;
}
